import * as React from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, userType: 'user' | 'ambassador', message?: string) => Promise<{ error: any; isAmbassadorRequest?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        console.log('Auth state change:', event, !!session);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      
      console.log('Initial session check:', !!session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string, userType: 'user' | 'ambassador', message?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    if (userType === 'ambassador') {
      // For ambassadors, create a regular user account and submit an ambassador request
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            user_type: 'user' // Create as regular user first
          }
        }
      });
      
      if (authError) return { error: authError };
      
      if (authData.user) {
        // Submit ambassador request
        const { error: requestError } = await supabase
          .from('ambassador_requests')
          .insert({
            user_id: authData.user.id,
            first_name: firstName,
            last_name: lastName,
            email: email,
            message: message || '',
          });
        
        if (requestError) return { error: requestError };
      }
      
      return { error: null, isAmbassadorRequest: true };
    } else {
      // Regular user signup
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            user_type: userType
          }
        }
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    // Record login history (best-effort, non-blocking)
    if (!error && data.user) {
      try {
        await supabase.from('login_history').insert({
          user_id: data.user.id,
          user_agent: navigator.userAgent,
          ip_address: null,
          location: null,
        } as any);
      } catch (e) {
        console.warn('Failed to record login history:', e);
      }
    }

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}